import { Component, OnInit, signal, inject, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { ComptageRuleService } from '../../../../core/services/comptage-rule.service';
import { ComptageRule } from '../../../../core/models/models';
import { ComptageRuleDialogComponent } from '../dialog/comptage-rule-dialog.component';

@Component({
    selector: 'app-comptage-rule-list',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatIconModule,
        MatTableModule,
        MatButtonModule,
        MatPaginatorModule,
        MatSortModule,
        ComptageRuleDialogComponent
    ],
    templateUrl: './comptage-rule-list.component.html',
    styleUrl: './comptage-rule-list.component.css'
})
export class ComptageRuleListComponent implements OnInit {
    private ruleService = inject(ComptageRuleService);

    @ViewChild(MatPaginator) paginator!: MatPaginator;
    @ViewChild(MatSort) sort!: MatSort;

    rules = signal<ComptageRule[]>([]);
    filteredRules = signal<ComptageRule[]>([]);
    pagedRules = signal<ComptageRule[]>([]);
    errorMessage = signal<string>('');
    isDialogOpen = signal<boolean>(false);
    selectedRule = signal<ComptageRule | undefined>(undefined);
    showArchived = signal<boolean>(false);
    totalElements = signal<number>(0);
    page = signal<number>(0);
    size = signal<number>(10);
    searchQuery = '';
    sortField = 'name';
    sortDirection: 'asc' | 'desc' = 'asc';

    ngOnInit(): void {
        this.loadRules();
    }

    loadRules(): void {
        this.ruleService.getAllRules(this.showArchived()).subscribe({
            next: (data) => {
                this.rules.set(data);
                this.applyFilters();
            },
            error: () => this.errorMessage.set('Erreur lors du chargement des règles')
        });
    }

    openCreateDialog(): void {
        this.selectedRule.set(undefined);
        this.isDialogOpen.set(true);
    }

    openEditDialog(rule: ComptageRule): void {
        this.selectedRule.set(rule);
        this.isDialogOpen.set(true);
    }

    closeDialog(): void {
        this.isDialogOpen.set(false);
    }

    onSaved(): void {
        this.closeDialog();
        this.loadRules();
    }

    onSearchChange(value: string): void {
        this.searchQuery = value;
        this.page.set(0);
        this.applyFilters();
    }

    onToggleArchived(): void {
        this.showArchived.update(v => !v);
        this.page.set(0);
        this.loadRules();
    }

    onSortChange(sortState: Sort): void {
        this.sortField = sortState.active;
        this.sortDirection = (sortState.direction || 'asc') as 'asc' | 'desc';
        this.page.set(0);
        this.applyFilters();
    }

    onPageChange(event: PageEvent): void {
        this.page.set(event.pageIndex);
        this.size.set(event.pageSize);
        this.updatePagedRules();
    }

    private applyFilters(): void {
        const query = this.searchQuery.trim().toLowerCase();
        const filtered = this.rules().filter(rule =>
            !query || rule.name.toLowerCase().includes(query)
        );

        const sorted = [...filtered].sort((a, b) => {
            const valueA = this.getSortValue(a, this.sortField);
            const valueB = this.getSortValue(b, this.sortField);
            if (valueA < valueB) return this.sortDirection === 'asc' ? -1 : 1;
            if (valueA > valueB) return this.sortDirection === 'asc' ? 1 : -1;
            return 0;
        });

        this.filteredRules.set(sorted);
        this.totalElements.set(sorted.length);
        this.updatePagedRules();
    }

    private updatePagedRules(): void {
        const total = this.filteredRules().length;
        const start = this.page() * this.size();
        if (start >= total && total > 0) {
            this.page.set(0);
        }
        const safeStart = this.page() * this.size();
        const end = safeStart + this.size();
        this.pagedRules.set(this.filteredRules().slice(safeStart, end));
    }

    private getSortValue(rule: ComptageRule, field: string): string {
        switch (field) {
            case 'name':
                return rule.name?.toLowerCase() || '';
            default:
                return rule.name?.toLowerCase() || '';
        }
    }

    onActivate(rule: ComptageRule, event: Event): void {
        event.stopPropagation();
        if (rule.id) {
            this.ruleService.activateRule(rule.id).subscribe({
                next: () => this.loadRules(),
                error: () => this.errorMessage.set('Erreur lors de l\'activation')
            });
        }
    }
}
